import React, { useState } from 'react';
import clsx from 'clsx';
import { FormField } from './FormField';
import type { FormFieldType } from '@builder4/shared/types/form';

// Item type for draggable form fields
export interface DraggableFormFieldItem {
  id: string;
  fieldType: FormFieldType;
  label?: string;
  isPrimaryEmailField?: boolean;
}

export interface DraggableFormFieldListProps {
  items: DraggableFormFieldItem[];
  className?: string;
  onReorder?: (next: DraggableFormFieldItem[]) => void; // reorder callback
  onFieldTypeChange?: (id: string, type: FormFieldType) => void;
  onEdit?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const DraggableFormFieldList: React.FC<DraggableFormFieldListProps> = ({
  items,
  className = '',
  onReorder,
  onFieldTypeChange,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const [draggingId, setDraggingId] = useState<string | undefined>();
  const [dragOver, setDragOver] = useState<
    { id: string; placement: 'before' | 'after' } | undefined
  >();

  const clearDrag = () => {
    setDraggingId(undefined);
    setDragOver(undefined);
  };

  const commitReorder = (
    sourceId: string,
    targetId: string,
    placement: 'before' | 'after' = 'before',
  ) => {
    if (!sourceId || !targetId || sourceId === targetId) {
      return;
    }
    const order = items.map((i) => i.id).filter((id) => id !== sourceId);
    const tgtIndex = order.indexOf(targetId);
    if (tgtIndex < 0) {
      return;
    }
    const insertIndex = placement === 'after' ? tgtIndex + 1 : tgtIndex;
    order.splice(insertIndex, 0, sourceId);
    const next = order.map((id) => items.find((i) => i.id === id)!) as DraggableFormFieldItem[];
    onReorder?.(next);
  };

  const placeholder = (targetId: string, placement: 'before' | 'after') => (
    <div
      key={`ph-${targetId}-${placement}`}
      className="h-9 w-full rounded-md border-2 border-dashed border-indigo-300 bg-indigo-50 opacity-50"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const sourceId = e.dataTransfer.getData('text/plain');
        if (sourceId) commitReorder(sourceId, targetId, placement);
        clearDrag();
      }}
    />
  );

  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      {items.map((item) => {
        const isDraggingItem = draggingId === item.id;
        const showBefore =
          draggingId && dragOver?.id === item.id && dragOver.placement === 'before';
        const showAfter = draggingId && dragOver?.id === item.id && dragOver.placement === 'after';
        return (
          <React.Fragment key={item.id}>
            {showBefore && placeholder(item.id, 'before')}
            <div
              className={clsx(isDraggingItem && 'opacity-70')}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', item.id);
                setDraggingId(item.id);
              }}
              onDragEnd={() => clearDrag()}
              onDragOver={(e) => {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const placement: 'before' | 'after' =
                  e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                setDragOver({ id: item.id, placement });
              }}
              onDrop={(e) => {
                e.preventDefault();
                const sourceId = e.dataTransfer.getData('text/plain');
                if (sourceId) commitReorder(sourceId, item.id, dragOver?.placement || 'before');
                clearDrag();
              }}
            >
              <FormField
                id={item.id}
                label={item.label ?? ''}
                fieldType={item.fieldType}
                selected={isDraggingItem}
                isPrimaryEmailField={item.isPrimaryEmailField}
                onFieldTypeChange={(type) => onFieldTypeChange?.(item.id, type)}
                onEdit={(id) => onEdit?.(id!)}
                onDuplicate={(id) => onDuplicate?.(id!)}
                onDelete={(id) => onDelete?.(id!)}
                draggable={false}
                className={clsx('pr-1', isDraggingItem && 'opacity-70')}
              />
            </div>
            {showAfter && placeholder(item.id, 'after')}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default DraggableFormFieldList;
